import { PivotItem } from "@fluentui/react";
import { Paper } from "@mui/material";
import { useContext, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import DisplayAPIsException from "../../../../BasicComponents/DisplayAPIsExceptionAlert";
import { logEvent, logException } from "../../../../helper/AppInsightsHelper";
import { ProductViewFiltersContext } from "../../../../pages/ProductView";
import { TabsContainer } from "../../../../SharedComponents/TabsContainer";
import { useGetFeatureProductDataDetailsMutation } from "../../../../slices/ExploreProducts.slice";
import { useGetUIEnablementsSettingsQuery } from "../../../../slices/Home.slice";
import { buildDisabledFeatureLookup } from "../../../../Utils/js-common";
import { PRODUCT_VIEW_DATA } from "../..//Common/ProductView.constants";
import { PRODUCT_VIEW_STYLES } from "../../Common/ProductView.styles";

export const FeatureTab = () => {
  const [enableTabs, setEnableTabs] = useState<boolean>(true);
  const {
    pivot: { ariaLabel },
    pivotItems
  } = PRODUCT_VIEW_DATA.TABS.TAB.FEATURE_VIEW.TABS_DATA;
  const uiEnablementData = useGetUIEnablementsSettingsQuery(undefined)?.data || [];
  const disabled = buildDisabledFeatureLookup(uiEnablementData);
  const disabledSubTabs = disabled["subTab"] || [];
  const filteredProductFamilies = pivotItems.filter(
    ({ headerText }) =>
      !disabledSubTabs
        .filter(({ feature, tab }) => feature === "Explore Products" && tab === "Features Telemetry")
        .map(({ subTab }) => subTab)
        .includes(headerText)
  );
  const renderTabs = filteredProductFamilies.map(({ headerText, itemKey, component }, index) => (
    <PivotItem headerText={headerText} itemKey={itemKey} key={`${itemKey + index}`}>
      {component()}
    </PivotItem>
  ));
  const filters = useContext(ProductViewFiltersContext);
  const [getData, { data, error, isError, isLoading, isSuccess }] = useGetFeatureProductDataDetailsMutation({
    fixedCacheKey: "getFeatures"
  });
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedTab = searchParams.get("section") || "Summary";
  const selectedTabKey = filteredProductFamilies.filter((tab) => tab.headerText === selectedTab)[0]?.itemKey;
  const customProps = {
    productName: filters?.productName,
    productFilters: JSON.stringify(filters)
  };

  // Events
  const handleSelectedTab = (item?: PivotItem) => {
    if (item?.props.headerText) {
      setSearchParams((prev) => {
        const newParams = new URLSearchParams(prev);
        newParams.set("section", item.props.headerText || "");
        return newParams;
      });
    }
  };

  // Hooks
  useEffect(() => {
    if (filters.productName) {
      getData({
        hasPartner: filters.hasPartner,
        industry: filters.industry,
        isCAT: filters.isCAT,
        isFastTrackStrategic: filters.isFastTrackStrategic,
        isSST: filters.isSST,
        productName: filters.productName,
        region: filters.region,
        segmentGroup: filters.segmentGroup,
        segment: filters.segment,
        subSegment: filters.subSegment,
        isS500: filters.isS500,
        isCCP: filters.isCCP,
        isTCT: filters.isTCT,
        majorGrowth: filters.majorGrowth
      });
    }
  }, [
    filters.hasPartner,
    filters.industry,
    filters.isCAT,
    filters.isFastTrackStrategic,
    filters.isSST,
    filters.productName,
    filters.region,
    filters.segmentGroup,
    filters.segment,
    filters.subSegment,
    filters.isS500,
    filters.isCCP,
    filters.isTCT,
    filters.majorGrowth,
    getData
  ]);
  useEffect(() => {
    if (isSuccess) {
      setEnableTabs(Boolean(data?.featuresByName.length));
    }
    if (isError) {
      logException("Features Telemetry", "FeatureTab", "getFeatureProductDataDetails", error, customProps);
    }
  }, [isSuccess, isError]);

  useEffect(() => {
    logEvent("Features Telemetry", "Product View Page", "FeaturesTelemetryTab", customProps);
  }, []);

  if (!isLoading && isError) {
    return <DisplayAPIsException error={error} height={250} />;
  }

  return (
    <Paper>
      {(isLoading || enableTabs) && (
        <TabsContainer ariaLabel={ariaLabel} handleSelectedTab={handleSelectedTab} selectedKey={selectedTabKey}>
          {renderTabs}
        </TabsContainer>
      )}
      {isSuccess && !enableTabs && <NoDataDisplayMessage productName={filters.productName} />}
    </Paper>
  );
};

const NoDataDisplayMessage = ({ productName }: { productName?: string }) => (
  <div className={PRODUCT_VIEW_STYLES.TAB.FEATURE_VIEW.CLASS_NAMES.noDataForProductDisplayMessage}>
    {productName} has no feature data available yet.
  </div>
);
